import classNames from 'classnames';
import { useSelector } from 'react-redux';
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import selectors from '../../../store/selectors';
import FormErrorMessageGeneral from '../../Form/formik/FormErrorMessage/FormErrorMessageGeneral';
import FormInput from '../../Form/formik/FormInput';
import { mobileOrHomePhoneRegexPattern, nameRegexPattern } from '../../../utils/string';

import styles from './PastoralVisitForm.module.scss';
import InputRegulationsAgree from './InputRegulationsAgree';
import SelectPreferredTimeslots from './SelectPreferredTimeslots';

// these names should match the same names in the backend
const formNames = {
  fullName: 'full_name',
  year: 'year',
  town: 'city',
  street: 'street',
  streetNumber: 'street_number',
  apartmentNumber: 'apartment_number',
  phone: 'phone',
  email: 'email',
  comments: 'comments',
  agreeOnRegulations: 'agreeOnRegulations',
  preferredTimeslot: 'preferred_time_slot',
};

const formSchema = ({ isPreferredTimeFeatureFlagEnabled }) =>
  Yup.object().shape({
    [formNames.fullName]: Yup.string()
      .matches(new RegExp(nameRegexPattern, 'u'), 'Podaj poprawne imię i nazwisko')
      .required('To pole jest wymagane'),
    [formNames.street]: Yup.string().required('To pole jest wymagane'),
    [formNames.streetNumber]: Yup.string().required('To pole jest wymagane'),
    [formNames.apartmentNumber]: Yup.number().typeError(
      'Podaj poprawny numer lokalu (tylko cyfry)',
    ),
    [formNames.town]: Yup.string().required('To pole jest wymagane'),
    [formNames.year]: Yup.string().required('To pole jest wymagane'),
    [formNames.phone]: Yup.string()
      .matches(
        // /(^((\+48)|(0048))?[ -]?[0-9]{3}[ -]?[0-9]{3}[ -]?[0-9]{3}$)|(^[0-9]{2}?[ -]?[0-9]{3}[ -]?[0-9]{2}[ -]?[0-9]{2}$)/
        new RegExp(mobileOrHomePhoneRegexPattern),
        'Podaj poprawny numer telefonu, np. w formacie 123 456 789 lub 22 123 12 12, lub +48 123 456 789',
      )
      .required('To pole jest wymagane'),
    [formNames.email]: Yup.string().email().required('To pole jest wymagane'),
    [formNames.comments]: Yup.string(),
    [formNames.agreeOnRegulations]: Yup.bool().oneOf(
      [true],
      'Musisz zgodzić się na regulamin, żeby zarezerwować Wizytę Duszpasterską przez Internet',
    ),
    ...(isPreferredTimeFeatureFlagEnabled && {
      [formNames.preferredTimeslot]: Yup.string().required('Wybierz preferowany termin wizyty'),
    }),
  });

const getFieldErrorNames = formikErrors => {
  const transformObjectToDotNotation = (obj, prefix = '', result = []) => {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (!value) return;

      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object') {
        transformObjectToDotNotation(value, nextKey, result);
      } else {
        result.push(nextKey);
      }
    });

    return result;
  };

  return transformObjectToDotNotation(formikErrors);
};

const ScrollToFieldError = () => {
  const { submitCount, errors } = useFormikContext();

  useEffect(() => {
    const fieldErrorNames = getFieldErrorNames(errors);
    if (fieldErrorNames.length <= 0) return;
    const element = document.querySelector(`input[name='${fieldErrorNames[0]}']`);
    if (!element) return;
    // Scroll to first known error into view
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Formik doesn't (yet) provide a callback for a client-failed submission,
    // thus why this is implemented through a hook that listens to changes on
    // the submit count.
  }, [submitCount]);

  return null;
};

const PastoralVisitForm = ({ onSubmit }) => {
  const parishData = useSelector(selectors.getAccountData);

  const pastoralVisitTextBeforeForm = useSelector(selectors.selectPastoralVisitTextBeforeForm);

  const isPreferredTimeFeatureFlagEnabled = useSelector(
    selectors.isPreferredTimeFeatureFlagEnabled,
  );
  const isPastoralVisitCityFeatureFlagEnabled = useSelector(
    selectors.isPastoralVisitCityFeatureFlagEnabled,
  );

  const initialValues = useMemo(() => {
    // if later than september, then current year, otherwise previous year
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const year = currentMonth > 8 ? currentYear : currentYear - 1;

    return {
      [formNames.fullName]: '',
      [formNames.street]: '',
      [formNames.streetNumber]: '',
      [formNames.apartmentNumber]: '',
      [formNames.town]: parishData.address.city,
      [formNames.year]: year,
      [formNames.phone]: '',
      [formNames.email]: '',
      [formNames.comments]: '',
      [formNames.agreeOnRegulations]: false,
      ...(isPreferredTimeFeatureFlagEnabled && { [formNames.preferredTimeslot]: '' }),
    };
  }, []);

  return (
    <div>
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: pastoralVisitTextBeforeForm }} />

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={formSchema({ isPreferredTimeFeatureFlagEnabled })}
      >
        {({ isSubmitting }) => (
          <Form autoComplete="on">
            <FormInput
              id={formNames.fullName}
              name={formNames.fullName}
              autoComplete="first-name"
              type="text"
              label="Imię i nazwisko"
            />

            <FormInput
              id={formNames.town}
              name={formNames.town}
              autoComplete="town"
              type="text"
              label="Miasto"
              isLabelActive
              hidden={!isPastoralVisitCityFeatureFlagEnabled}
            />

            <FormInput
              wrapperClassName="mb-2"
              id={formNames.street}
              name={formNames.street}
              autoComplete="address"
              type="text"
              label="Ulica"
            />

            <div className={classNames(styles.inputRowWrapper, 'd-flex align-items-start')}>
              <FormInput
                wrapperClassName="mb-2"
                id={formNames.streetNumber}
                name={formNames.streetNumber}
                autoComplete="address.number"
                type="text"
                label="Numer budynku"
              />

              <FormInput
                wrapperClassName="mb-2"
                id={formNames.apartmentNumber}
                name={formNames.apartmentNumber}
                autoComplete="address.flatNumber"
                type="text"
                label="Numer lokalu (opcjonalnie)"
              />
            </div>

            <FormInput
              id={formNames.phone}
              name={formNames.phone}
              autoComplete="phone"
              inputMode="tel"
              type="text"
              label="Telefon"
            />

            <FormInput
              id={formNames.email}
              name={formNames.email}
              autoComplete="email"
              inputMode="email"
              type="email"
              label="Email"
            />

            {isPreferredTimeFeatureFlagEnabled && (
              <SelectPreferredTimeslots name={formNames.preferredTimeslot} />
            )}

            <FormInput
              id={formNames.comments}
              name={formNames.comments}
              type="text"
              label="Uwagi (opcjonalnie)"
            />

            <InputRegulationsAgree
              id={formNames.agreeOnRegulations}
              name={formNames.agreeOnRegulations}
            />

            <div className="md-form input-group">
              <p className="grey-text">
                <small>
                  <em>
                    Po dokonaniu zapisu dodamy Cię na listę osób, które odwiedzimy w ramach
                    najbliższej wizyty duszpasterskiej.
                  </em>
                </small>
              </p>
              <ScrollToFieldError />
              <FormErrorMessageGeneral />
              <Button
                loading={isSubmitting}
                type="primary"
                htmlType="submit"
                className={styles.submitButton}
              >
                Zapisuję się na wizytę duszpasterską
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

PastoralVisitForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default PastoralVisitForm;
